import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useParams } from 'react-router-dom'
import { DownloadIcon, PlusIcon } from 'assets/icons'
import { partialContentUpdate } from 'api/content'
import { upload } from 'api/aws'
import Sidebar from 'components/Sidebar'
import DragDrop from 'components/UI/DragDrop'
import useContent from 'hooks/api/useContent'
import { t, wait } from 'helpers'
import { notif } from 'helpers/notif'
import { useProductionStore } from 'store'

const NdaSidebar = (): JSX.Element => {
	const [openSidebar, setOpenSidebar] = useProductionStore(
		useShallow((state) => [state.openNdaSidebar, state.setOpenNdaSidebar]),
	)
	const { productionId } = useParams()
	const [file, setFile] = useState<File | null>(null)
	const [loading, setLoading] = useState(false)

	const {
		content: prodFull,
		isLoading,
		refreshData,
	} = useContent(productionId, 'full')
	const prodNda = prodFull?.assets?.filter(
		(asset) => !asset?.isDeleted && asset.publicUrl?.endsWith('.pdf'),
	)

	const confirmNda = async (): Promise<void> => {
		try {
			const { setNdaFile, createAsset, onChangeEvent } =
				useProductionStore.getState()
			setNdaFile(file)
			if (productionId && file) {
				const uploadedFile = await upload({
					fileObj: file,
					assetType: 'Content',
				})
				await createAsset(
					uploadedFile,
					`NDA - ${file.name}`,
					uploadedFile.originalFileName,
					uploadedFile.fileAssetUrl,
					uploadedFile.cdnUrl,
					productionId,
				)

				onChangeEvent('nda', true as unknown as string, productionId)
			} else if (file) {
				onChangeEvent('nda', true as unknown as string)
			}
		} catch (error) {
			console.log('error:', error)
		} finally {
			setOpenSidebar(false)
		}
	}

	useEffect(() => {
		return () => {
			setFile(null)
		}
	}, [])

	return (
		<Sidebar
			open={openSidebar}
			onClose={() => setOpenSidebar(false)}
			header={{
				title: 'APP_NDA_SIDEBAR_TITLE',
				description: 'APP_NDA_SIDEBAR_DESCRIPTION',
			}}
			loading={isLoading || loading}
			buttons={[
				{
					children: t('APP_DONE'),
					onClick: () => {
						if (file) {
							confirmNda()
						} else {
							notif('info', t('APP_FILL_FIELDS'))
						}
					},
				},
			]}>
			<>
				{prodNda?.map((nda) => (
					<div
						key={nda.id}
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: '10px',
						}}>
						<DownloadIcon />
						<a
							className="body1-b"
							href={nda?.publicUrl}
							target="_blank"
							download
							style={{
								textDecoration: 'none',
								textTransform: 'none',
								color: 'var(--mono100)',
							}}>
							{t('APP_DOWNLOAD_PRODUCTION_NDA')}
						</a>
					</div>
				))}
				<DragDrop
					hint="APP_DRAG_&_DROP"
					name="nda-attachments"
					accept=".doc, .docx, application/pdf"
					onChange={(e) => {
						if (e.target.files?.length) setFile(e.target.files[0])
					}}
					disabled={!!prodNda?.length}
				/>
				{[file, ...(prodNda || [])]
					.filter((v) => !!v)
					.map((asset, i) => (
						<div
							key={asset ? ('id' in asset ? asset?.id : asset.name) : i}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
							}}>
							<span className="textEllipsis">
								{asset && 'id' in asset ? asset?.fileName : file?.name}
							</span>
							<div
								style={{ display: 'flex', cursor: 'pointer' }}
								onClick={async () => {
									if (file) {
										setFile(null)
									} else if (prodNda && prodFull?.ContentId) {
										setLoading(true)
										try {
											await partialContentUpdate(prodFull?.ContentId, {
												assets: prodFull?.assets?.map((a) => {
													if (asset && 'id' in asset && a.id === asset.id) {
														return {
															...a,
															isDeleted: true,
														}
													} else return a
												}),
											})

											await wait(1000)
											refreshData()
											const { onChangeEvent } = useProductionStore.getState()
											onChangeEvent(
												'nda',
												false as unknown as string,
												productionId,
											)
										} catch (error) {
											console.log('error:', error)
										} finally {
											setLoading(false)
										}
									}
								}}>
								<PlusIcon
									style={{
										transform: 'rotate(45deg)',
										marginLeft: 'auto',
									}}
								/>
							</div>
						</div>
					))}
			</>
		</Sidebar>
	)
}

export default NdaSidebar
